import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Spinner } from '@fluentui/react-components';
import { loginRequest } from './config/authConfig';
import AppLayout from './components/layout/AppLayout';
import DashboardPage from './pages/DashboardPage';
import TaxReturnsPage from './pages/TaxReturnsPage';
import TaxReturnDetailPage from './pages/TaxReturnDetailPage';
import OutreachPage from './pages/OutreachPage';
import PriorityOutreachPage from './pages/PriorityOutreachPage';
import WorkloadPage from './pages/WorkloadPage';
import PreparerBillingPage from './pages/PreparerBillingPage';
import CalendarPage from './pages/CalendarPage';
import ClientOptOutsPage from './pages/ClientOptOutsPage';
import UploadPortalPage from './pages/UploadPortalPage';
import DrakeUploadPage from './pages/DrakeUploadPage';
import ClientUploadsPage from './pages/ClientUploadsPage';
import OutreachDashboardPage from './pages/OutreachDashboardPage';
import InquiriesPage from './pages/InquiriesPage';
import IrsNoticesPage from './pages/IrsNoticesPage';
import InHouseClientsPage from './pages/InHouseClientsPage';
import SettingsPage from './pages/SettingsPage';
import StaffRolesPage from './pages/StaffRolesPage';
import PatchNotesPage from './pages/PatchNotesPage';
import TaxClientsPage from './pages/TaxClientsPage';
import FilingHistoryImportPage from './pages/admin/FilingHistoryImportPage';
import MatchReviewPage from './pages/admin/MatchReviewPage';

// Lazy load dialog components to reduce initial bundle size
// Export for use in other components - these are loaded on-demand when needed
export const LazySettingsDialog = lazy(() => import('./components/SettingsDialog').then(m => ({ default: m.SettingsDialog })));

// Dialog loading fallback component
const DialogFallback = () => <Spinner size="small" />;

// Helper to wrap lazy dialogs in Suspense
export function LazyDialog({ show, children }: { show: boolean; children: React.ReactNode }) {
  if (!show) return null;
  return <Suspense fallback={<DialogFallback />}>{children}</Suspense>;
}

// Public routes that don't require authentication
const publicRoutes = ['/portal'];

// Portal domain - when users access via this domain, go straight to portal
const portalDomains = ['portal.ptc-financial.com'];

function App() {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  // Check if accessing from portal domain - redirect to /portal if not already there
  const isPortalDomain = portalDomains.includes(window.location.hostname);
  if (isPortalDomain && !location.pathname.startsWith('/portal')) {
    return <Navigate to="/portal" replace />;
  }

  // Check if current path is a public route
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

  // Public routes don't need authentication
  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/portal" element={<UploadPortalPage />} />
      </Routes>
    );
  }

  // Show loading while checking auth status
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="loading-container" style={{ height: '100vh' }}>
        <Spinner size="large" label="Authenticating..." />
      </div>
    );
  }

  // If not authenticated, trigger login
  if (!isAuthenticated) {
    instance.loginRedirect(loginRequest).catch(console.error);
    return (
      <div className="loading-container" style={{ height: '100vh' }}>
        <Spinner size="large" label="Redirecting to login..." />
      </div>
    );
  }

  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/tax-returns" element={<TaxReturnsPage />} />
        <Route path="/tax-returns/:id" element={<TaxReturnDetailPage />} />
        <Route path="/outreach" element={<OutreachPage />} />
        <Route path="/priority-outreach" element={<PriorityOutreachPage />} />
        <Route path="/outreach-dashboard" element={<OutreachDashboardPage />} />
        <Route path="/inquiries" element={<InquiriesPage />} />
        <Route path="/irs-notices" element={<IrsNoticesPage />} />
        <Route path="/workload" element={<WorkloadPage />} />
        <Route path="/preparer-billing" element={<PreparerBillingPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/client-opt-outs" element={<ClientOptOutsPage />} />
        <Route path="/inhouse-clients" element={<InHouseClientsPage />} />
        <Route path="/tax-clients" element={<TaxClientsPage />} />
        <Route path="/drake" element={<DrakeUploadPage />} />
        <Route path="/client-uploads" element={<ClientUploadsPage />} />
        <Route path="/staff-roles" element={<StaffRolesPage />} />
        <Route path="/patch-notes" element={<PatchNotesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/admin/imports" element={<FilingHistoryImportPage />} />
        <Route path="/admin/match-review" element={<MatchReviewPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
}

export default App;
